import { Suspense } from "react";
import { Navigate, useParams, useRoutes, useSearchParams } from "react-router";
import { BaseShell, HeaderLessShell, MiniShell } from "../shell/Shell.jsx";
import { Spinner } from "../../design-system/utils/Spinner/Spinner.jsx";
import LibraryOverview from "../library/LibraryOverview.jsx";
import FullLibraryItem from "../library/components/FullLibraryItem/FullLibraryItem.jsx";
import TeamDiary from "../diary/TeamDiary.jsx";
import NotFoundPage from "../not-found/NotFoundPage.jsx";
import RouteBoundary from "../../lib/RouteBoundary";
import { ROUTES } from "../../routes.js";
import { MODELS, useModel } from "../../api";
import useShowToolbar from "../../hooks/useShowToolbar.jsx";
import { flags } from "../../flags";
import SparkStepOverview from "./spark-step/SparkStepOverview.jsx";
import SparkShowroomOverview from "./showroom/SparkShowroomOverview.jsx";
import GlossaryOverview from "./glossary/GlossaryOverview";
import { ToolbarContainer } from "./toolbar/ToolbarContainer.jsx";
import { ClassroomManagementOverlay } from "./classroom-management/ClassroomManagementOverlay";
import OptionsOverview from "./workshop-settings/OptionsOverview.jsx";
import { WorkshopUserTour } from "./user-tour/WorkshopUserTour.jsx";
import SparkOverview from "./spark-overview/SparkOverview.jsx";
import NewSparkOverview from "./new-spark-overview/SparkOverview";
import InviteOverview from "./invites/InviteOverview.jsx";
import { SparkProvider } from "./context/SparkContext.jsx";
import FullShowroomEntry from "./showroom/components/FullShowroomEntry/FullShowroomEntry.jsx";

// Todo: Add "getUrl" functions, use these in the toolbar etc.
const routes = [
  {
    path: "/",
    element: flags.newSparkOverview ? <NewSparkOverview /> : <SparkOverview />,
  },
  { path: "/step/:stepSlug", element: <SparkStepOverview /> },
  { path: "/showroom", element: <SparkShowroomOverview /> },
  { path: "/showroom/:smallTeamId", element: <FullShowroomEntry /> },
  { path: "/library", element: <LibraryOverview /> },
  { path: "/library/:libraryItemId", element: <FullLibraryItem /> },
  { path: "/team-diary/:smallTeamId", element: <TeamDiary /> },
  { path: "/invite", element: <InviteOverview /> },
  { path: "/options", element: <OptionsOverview /> },
  { path: "/glossary", element: <GlossaryOverview /> },
  // Unknown sub-route is an explicit, in-shell 404 — no silent redirect.
  { path: "*", element: <NotFoundPage /> },
];

function SparkShell() {
  const { workshopSlug, sparkSlug: urlSparkSlug } = useParams();
  // Suspends while loading; a bad workshop slug 404s to the outer RouteBoundary.
  const workshop = useModel(MODELS.WORKSHOP({ slug: workshopSlug }));
  const spark = workshop.spark;
  const structure = spark?.structure;
  const settings = workshop.settings;

  const [searchParams] = useSearchParams();
  const shouldShowTour = searchParams.has("tour");

  const shouldShowToolbar = useShowToolbar();

  const element = useRoutes(routes);

  // A workshop without a spark is broken data, not a route the user can fix.
  if (!spark) {
    return (
      <HeaderLessShell>
        <NotFoundPage />
      </HeaderLessShell>
    );
  }

  // Redirect if we have an outdated url from back when a workshop could have
  // multiple sparks — a real redirect to the canonical spark, not a 404.
  if (urlSparkSlug !== spark.slug) {
    return (
      <Navigate
        to={ROUTES.spark.getUrl({ workshopSlug, sparkSlug: spark.slug })}
        replace
      />
    );
  }

  // In all other cases, render the correct component for the specified route.
  return (
    <SparkProvider
      workshop={workshop}
      spark={spark}
      structure={structure}
      settings={settings}
    >
      <ClassroomManagementOverlay />

      <BaseShell
        bottom={
          shouldShowToolbar && (
            // Own Suspense so the toolbar's small-team fetch never blanks the shell.
            <Suspense fallback={null}>
              <ToolbarContainer />
            </Suspense>
          )
        }
      >
        {shouldShowTour && <WorkshopUserTour />}
        <RouteBoundary Shell={MiniShell}>
          <Suspense fallback={<Spinner />}>{element}</Suspense>
        </RouteBoundary>
      </BaseShell>
    </SparkProvider>
  );
}

export default SparkShell;
